<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import Modal from "@/helpers/modal";
import Download from "@/helpers/download";
import Loader from "@/helpers/loader";
import Permission from "@/helpers/permission";
import ChargeNegotiationModal from "@/views/subscriber/ChargeNegotiationModal.vue";
import ChargeNegotiationDetailsModal from "@/views/subscriber/ChargeNegotiationDetailsModal.vue";
import { Date as BrDate } from "@/helpers/date";
import '@/views/calendar/workspace/scheduleWorkspace.css';

export default {
  name: "ChargesModal",
  props: ['subscriber'],
  emits: ['onClose', 'openPixModal', 'chargesUpdated'],
  components: { ChargeNegotiationModal, ChargeNegotiationDetailsModal },
  data() {
    return {
      subscriberLocal: null,
      carnetPollTimer: null,
      activeTab: 'all',
      extractUrl: null,
      extractLinkLoading: false,
    }
  },
  computed: {
    chargeTabs() {
      return [
        { id: 'all', label: 'Todos', statuses: [] },
        { id: 'receivable', label: 'A receber', statuses: ['pending', 'overdue', 'payment_failed'] },
        { id: 'received', label: 'Recebidos', statuses: ['paid'] },
        { id: 'cancelled', label: 'Cancelados', statuses: ['cancelled'] },
        { id: 'unfolded', label: 'Desdobrados', statuses: ['negotiated'] },
      ];
    },
    /** Parcelas agrupadas por aba; `originalIndex` aponta para a posição em `subscriberLocal.charges`. */
    chargesByTab() {
      const charges = this.subscriberLocal?.charges || [];
      const groups = Object.fromEntries(this.chargeTabs.map((tab) => [tab.id, []]));

      charges.forEach((charge, originalIndex) => {
        groups.all.push({ charge, originalIndex });
        const tab = this.chargeTabs.find((t) => t.statuses.includes(charge.charge_status));
        if (tab) {
          groups[tab.id].push({ charge, originalIndex });
        }
      });

      Object.values(groups).forEach((items) => {
        items.sort((a, b) => new Date(a.charge.charge_due_date) - new Date(b.charge.charge_due_date));
      });

      return groups;
    },
    filteredCharges() {
      return this.chargesByTab[this.activeTab] || [];
    },
    place() {
      return this.$store.state.auth.place;
    },
    isAdmin() {
      return !!this.$store.state.auth.user?.is_admin;
    },
    canNegotiate() {
      return Permission.has('Negotiate Subscriber Charges');
    },
    /** Parcelas que o backend considera para reprocessar (pendentes / vencidas / falha). */
    eligibleReprocessCharges() {
      const list = this.subscriberLocal?.charges || [];
      return list.filter((c) => {
        const s = c.charge_status;
        if (s === 'cancelled' || s === 'negotiated' || s === 'paid') return false;
        return ['pending', 'overdue', 'payment_failed'].includes(s);
      });
    },
    /** Boleto: parcela elegível sem boleto / com falha. */
    needsBankSlipReprocessButton() {
      if (this.subscriberLocal?.plan_payment_method !== 'bank_slip') return false;
      const eligible = this.eligibleReprocessCharges;
      if (!eligible.length) return false;
      return eligible.some((c) => {
        if (c.charge_status === 'payment_failed') return true;
        if (!c.id_card_trans) return true;
        return !c.card_transaction?.boleto_url;
      });
    },
    /** PIX: alinhado ao fluxo mensal (1ª parcela vigente) ou à vista (qualquer PIX nas elegíveis). */
    needsPixReprocessButton() {
      if (this.subscriberLocal?.plan_payment_method !== 'pix') return false;
      const eligible = this.eligibleReprocessCharges;
      if (!eligible.length) return false;
      const billingMode = this.subscriberLocal.billing_mode || 'monthly';
      if (billingMode === 'monthly') {
        const ordered = [...eligible].sort(
          (a, b) => new Date(a.charge_due_date) - new Date(b.charge_due_date)
        );
        const first = ordered[0];
        if (first.charge_status === 'payment_failed') return true;
        const hasPix = !!(first.card_transaction?.pix_code);
        if (first.id_card_trans && hasPix) return false;
        return true;
      }
      const hasAnyPix = eligible.some((c) => c.card_transaction?.pix_code);
      if (!hasAnyPix) return true;
      return eligible.some((c) => c.charge_status === 'payment_failed');
    },
    canShowReprocessPayment() {
      return this.isAdmin && (this.needsBankSlipReprocessButton || this.needsPixReprocessButton);
    },
  },
  beforeUnmount() {
    this.clearCarnetPoll();
  },
  watch: {
    subscriber: {
      immediate: true,
      handler(newValue) {
        this.subscriberLocal = newValue;
        this.extractUrl = null;
      }
    }
  },
  methods: {
    formatDate(date) {
      return BrDate.formatLocalDate(date);
    },
    async copyExtractLink() {
      if (this.extractLinkLoading) return;

      try {
        if (!this.extractUrl) {
          this.extractLinkLoading = true;
          const response = await http.get(
            `/places/${this.place.id_place}/subscribers/${this.subscriberLocal.id_subscriber}/charges/extract-link`
          );
          this.extractUrl = response.data?.url;
        }

        if (!this.extractUrl) {
          Alert.toastError('Não foi possível gerar o link do extrato.');
          return;
        }

        await navigator.clipboard.writeText(this.extractUrl);
        Alert.toastSuccess('Link do extrato copiado. Ele vale por 7 dias.');
      } catch (error) {
        Alert.toastError(error.response?.data?.message || 'Não foi possível copiar o link do extrato.');
      } finally {
        this.extractLinkLoading = false;
      }
    },
    clearCarnetPoll() {
      if (this.carnetPollTimer) {
        clearInterval(this.carnetPollTimer);
        this.carnetPollTimer = null;
      }
    },
    canChangeDueDate(charge) {
      if ((charge?.plan_payment_method || this.subscriberLocal?.plan_payment_method) !== 'bank_slip') return false;
      if (!charge) return false;
      if (['cancelled', 'negotiated', 'paid'].includes(charge.charge_status)) return false;
      return ['pending', 'overdue', 'payment_failed'].includes(charge.charge_status);
    },
    canChangePaymentMethod(charge) {
      if (!charge) return false;
      if (['cancelled', 'negotiated', 'paid'].includes(charge.charge_status)) return false;
      return ['pending', 'overdue', 'payment_failed'].includes(charge.charge_status);
    },
    paymentMethodLabel(value) {
      const map = {
        credit_card: 'Cartão de crédito',
        bank_slip: 'Boleto',
        pix: 'PIX',
        money: 'Dinheiro',
      };
      return map[value] || value || '-';
    },
    chargePaymentMethod(charge) {
      return charge?.plan_payment_method || this.subscriberLocal?.plan_payment_method || null;
    },
    async changeChargePaymentMethod(charge, index) {
      const current = this.chargePaymentMethod(charge) || 'money';
      const options = [
        { value: 'pix', label: 'PIX' },
        { value: 'bank_slip', label: 'Boleto' },
        { value: 'money', label: 'Dinheiro' },
        { value: 'credit_card', label: 'Cartão de crédito' },
      ];
      const optionsHtml = options.map((o) =>
        `<option value="${o.value}"${o.value === current ? ' selected' : ''}>${o.label}</option>`
      ).join('');

      const result = await Swal.fire({
        title: 'Alterar forma de pagamento',
        html: `
          <p class="text-start text-muted small mb-2">Altera somente esta parcela. As demais continuam com a forma da assinatura.</p>
          <label for="swal-charge-payment-method" class="form-label text-start d-block">Forma de pagamento</label>
          <select id="swal-charge-payment-method" class="form-select">${optionsHtml}</select>
        `,
        showCancelButton: true,
        confirmButtonText: 'Alterar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#780f5a',
        focusConfirm: false,
        preConfirm: () => {
          const v = document.getElementById('swal-charge-payment-method')?.value;
          if (!v) {
            Swal.showValidationMessage('Selecione a forma de pagamento');
            return false;
          }
          return v;
        },
      });

      if (!result.isConfirmed || !result.value) return;
      if (result.value === current) return;

      Loader.show('Alterando forma de pagamento', 'Aguarde');
      try {
        const response = await http.put(
          `/places/${this.place.id_place}/subscribers/${this.subscriberLocal.id_subscriber}/charges/${charge.id_charge}/payment-method`,
          { plan_payment_method: result.value }
        );

        if (response.data?.subscriber) {
          this.subscriberLocal = {
            ...this.subscriberLocal,
            ...response.data.subscriber,
            charges: response.data.subscriber.charges || this.subscriberLocal.charges,
          };
        } else if (response.data?.data) {
          this.subscriberLocal.charges[index] = response.data.data;
        }

        Alert.success(response.data?.message || 'Forma de pagamento atualizada.');

        const pixData = response.data?.pix_data;
        if (result.value === 'pix' && pixData?.pix_code) {
          this.$emit('openPixModal', {
            pix_code: pixData.pix_code,
            qr_code_base64: pixData.qr_code_base64 || pixData.pix_qrcode_img,
            payment_id: pixData.transaction_id || pixData.id_card_trans,
            subscription_id: this.subscriberLocal?.id_subscriber,
            expires_at: pixData.expires_at || pixData.pix_expires_at || null,
          });
        }

        this.$emit('chargesUpdated');
      } catch (error) {
        Alert.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao alterar forma de pagamento.');
      } finally {
        Loader.close();
      }
    },
    async changeChargeDueDate(charge, index) {
      const current = (charge.charge_due_date || '').toString().slice(0, 10);
      const today = new Date().toISOString().slice(0, 10);
      const defaultDate = current > today ? current : today;

      const result = await Swal.fire({
        title: 'Alterar vencimento',
        html: `
          <div class="text-start" style="width:100%;max-width:100%;overflow:hidden;box-sizing:border-box;">
            <p class="mb-3" style="margin:0 0 1rem;">O boleto atual será cancelado e um novo será gerado com a data informada.</p>
            <label for="swal-due-date" class="form-label">Nova data de vencimento</label>
            <input id="swal-due-date" type="date" class="form-control" min="${today}" value="${defaultDate}" style="width:100%;max-width:100%;box-sizing:border-box;margin:0;">
          </div>
        `,
        width: 420,
        showCancelButton: true,
        confirmButtonText: 'Alterar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#780f5a',
        focusConfirm: false,
        preConfirm: () => {
          const value = document.getElementById('swal-due-date')?.value;
          if (!value) {
            Swal.showValidationMessage('Informe a nova data de vencimento.');
            return false;
          }
          if (value < today) {
            Swal.showValidationMessage('A data não pode ser anterior a hoje.');
            return false;
          }
          return value;
        },
      });

      if (!result.isConfirmed || !result.value) return;

      Loader.show('Alterando vencimento', 'Cancelando boleto e gerando novo...');
      try {
        const response = await http.put(
          `/places/${this.place.id_place}/subscribers/${this.subscriberLocal.id_subscriber}/charges/${charge.id_charge}/due-date`,
          { due_date: result.value }
        );

        if (response.data?.subscriber) {
          this.subscriberLocal = {
            ...this.subscriberLocal,
            ...response.data.subscriber,
            charges: response.data.subscriber.charges || this.subscriberLocal.charges,
          };
        } else if (response.data?.data) {
          this.subscriberLocal.charges[index] = response.data.data;
        }

        Alert.success(response.data?.message || 'Vencimento alterado com sucesso.');
        this.$emit('chargesUpdated');
      } catch (error) {
        Alert.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao alterar vencimento.');
      } finally {
        Loader.close();
      }
    },
    async downloadInstallmentCharge(charge) {
      const method = this.chargePaymentMethod(charge);
      if (method === 'pix' && charge?.charge_status !== 'paid' && charge?.charge_status !== 'cancelled') {
        await this.payChargeWithPix(charge);
        return;
      }

      if (method === 'bank_slip' && charge?.card_transaction?.boleto_url) {
        window.open(charge.card_transaction.boleto_url, '_blank');
        return;
      }

      Loader.show('Gerando PDF', 'Aguarde');

      http.get(`/places/${this.place.id_place}/subscribers/${this.subscriber.id_subscriber}/charges/${charge.id_charge}/print`, {responseType: 'blob'})
        .then((response) => {
          Download.download(response.data);
          Loader.close();
        })
        .catch(() => {
          Loader.close();
          Alert.error('Erro ao gerar o PDF da parcela.');
        });
    },
    async payChargeWithPix(charge) {
      Loader.show('Gerando PIX', 'Aguarde');

      try {
        const response = await http.post(`/places/${this.place.id_place}/subscribers/${this.subscriber.id_subscriber}/charges/${charge.id_charge}/pay`);
        const pixData = response.data?.pix_data;
        const paymentId = pixData?.id_card_trans || pixData?.transaction_id;
        const qrCodeBase64 = pixData?.pix_qrcode_img || pixData?.qr_code_base64;

        if (!paymentId || !pixData?.pix_code) {
          Alert.error('Não foi possível gerar o PIX para esta cobrança.');
          return;
        }

        this.$emit('openPixModal', {
          pix_code: pixData.pix_code,
          qr_code_base64: qrCodeBase64,
          payment_id: paymentId,
          subscription_id: this.subscriber?.id_subscriber,
          expires_at: pixData.expires_at || pixData.pix_expires_at || null,
        });
      } catch (error) {
        Alert.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao gerar PIX para esta cobrança.');
      } finally {
        Loader.close();
      }
    },
    async downloadReceiptsCharge() {
      Loader.show('Gerando comprovantes', 'Aguarde');

      http.get(`/places/${this.place.id_place}/subscribers/${this.subscriber.id_subscriber}/charges/receipt`, {responseType: 'blob'})
        .then((response) => {
          Download.download(response.data)
        }).finally(() => {
        Loader.close();
      });
    },
    async downloadReceiptCharge(charge) {
      Loader.show('Gerando comprovante', 'Aguarde');

      http.get(`/places/${this.place.id_place}/subscribers/${this.subscriber.id_subscriber}/charges/${charge.id_charge}/receipt`, {responseType: 'blob'})
        .then((response) => {
          Download.download(response.data)
        }).finally(() => {
          Loader.close();
        });
    },
    async downloadInstallmentsCharge() {
      if (this.subscriberLocal?.plan_payment_method === 'bank_slip') {
        await this.downloadBankSlipCarnet();
      } else if (this.subscriberLocal?.plan_payment_method === 'money') {
        this.downloadInternalCarnet();
      } else {
        Alert.toastInfo('Carnê não disponível para este método de pagamento.');
      }
    },

    async downloadBankSlipCarnet() {
      Loader.show('Preparando carnê', 'Aguarde');
      this.clearCarnetPoll();

      try {
        const response = await http.get(
          `/places/${this.place.id_place}/subscribers/${this.subscriberLocal.id_subscriber}/charges/carnet`
        );

        const status = response.data?.status;
        const url = response.data?.carnet_url;
        const message = response.data?.message;

        if (response.data?.subscriber) {
          this.subscriberLocal = {
            ...this.subscriberLocal,
            ...response.data.subscriber,
            charges: response.data.subscriber.charges || this.subscriberLocal.charges,
          };
        }

        if (status === 'ready' && url) {
          window.open(url, '_blank');
          Alert.success('Carnê aberto em nova aba.');
          return;
        }

        if (status === 'pending') {
          Alert.toastInfo(message || 'Carnê sendo gerado, aguarde.');
          this.startCarnetPoll();
          return;
        }

        Alert.error(message || 'Carnê não disponível.');
      } catch (error) {
        Alert.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao solicitar o carnê.');
      } finally {
        Loader.close();
      }
    },

    startCarnetPoll() {
      this.clearCarnetPoll();
      let attempts = 0;
      this.carnetPollTimer = setInterval(async () => {
        attempts += 1;
        if (attempts > 20) {
          this.clearCarnetPoll();
          Alert.error('Tempo esgotado aguardando o carnê. Tente novamente em instantes.');
          return;
        }

        try {
          const response = await http.get(
            `/places/${this.place.id_place}/subscribers/${this.subscriberLocal.id_subscriber}/charges/carnet`
          );

          if (response.data?.subscriber) {
            this.subscriberLocal = {
              ...this.subscriberLocal,
              ...response.data.subscriber,
              charges: response.data.subscriber.charges || this.subscriberLocal.charges,
            };
          }

          if (response.data?.status === 'ready' && response.data?.carnet_url) {
            this.clearCarnetPoll();
            window.open(response.data.carnet_url, '_blank');
            Alert.success('Carnê pronto.');
          } else if (response.data?.status === 'failed') {
            this.clearCarnetPoll();
            Alert.error(response.data?.message || 'Falha ao gerar o carnê.');
          }
        } catch (e) {
          // mantém polling
        }
      }, 2500);
    },

    async downloadInternalCarnet() {
      Loader.show('Gerando PDF', 'Aguarde');

      http.get(`/places/${this.place.id_place}/subscribers/${this.subscriber.id_subscriber}/charges/print`, {responseType: 'blob'})
        .then((response) => {
          Download.download(response.data)
        }).finally(() => {
          Loader.close();
        });
    },
    paidPaymentMethodLabel(value) {
      return this.paymentMethodLabel(value);
    },
    manualDownUserName(charge) {
      return charge?.user_manual_down?.user_name || '-';
    },
    async downCharge(charge, index) {
      const defaultMethod = this.chargePaymentMethod(charge) || 'money';
      const options = [
        { value: 'pix', label: 'PIX' },
        { value: 'bank_slip', label: 'Boleto' },
        { value: 'money', label: 'Dinheiro' },
        { value: 'credit_card', label: 'Cartão de crédito' },
      ];
      const optionsHtml = options.map((o) =>
        `<option value="${o.value}"${o.value === defaultMethod ? ' selected' : ''}>${o.label}</option>`
      ).join('');

      const result = await Swal.fire({
        title: 'Baixa manual',
        html: `
          <p class="text-start text-muted small mb-2">Informe como esta parcela foi quitada (pode ser diferente da forma prevista na assinatura).</p>
          <label for="swal-paid-payment-method" class="form-label text-start d-block">Forma de pagamento</label>
          <select id="swal-paid-payment-method" class="form-select">${optionsHtml}</select>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Confirmar baixa',
        cancelButtonText: 'Cancelar',
        focusConfirm: false,
        preConfirm: () => {
          const el = document.getElementById('swal-paid-payment-method');
          const v = el?.value;
          if (!v) {
            Swal.showValidationMessage('Selecione a forma de pagamento');
            return false;
          }
          return v;
        },
      });

      if (!result.isConfirmed) {
        return;
      }

      Loader.show('Registrando baixa...', 'Aguarde');
      try {
        const response = await http.put(
          `/places/${this.place.id_place}/subscribers/${this.subscriber.id_subscriber}/charges/${charge.id_charge}/down`,
          { paid_payment_method: result.value }
        );
        this.subscriberLocal.charges[index] = response.data;
        Alert.success('Operação realizada com sucesso.');
        this.$emit('chargesUpdated');
      } catch (e) {
        const msg = e.response?.data?.message
          || (typeof e.response?.data === 'string' ? e.response.data : null)
          || e.response?.data?.errors?.paid_payment_method?.[0];
        Alert.error(msg || 'Erro ao registrar baixa.');
      } finally {
        Loader.close();
      }
    },
    async undownCharge(charge, index) {
      http.put(`/places/${this.place.id_place}/subscribers/${this.subscriber.id_subscriber}/charges/${charge.id_charge}/undown`)
        .then((response) => {
          this.subscriberLocal.charges[index] = response.data;
          Alert.success('Operação realizada com sucesso.');
          this.$emit('chargesUpdated');
        });
    },
    statusClass(status) {
      switch (status) {
        case 'paid': return 'badge bg-success';
        case 'pending': return 'badge bg-warning text-dark';
        case 'overdue': return 'badge bg-danger';
        case 'cancelled': return 'badge bg-info';
        default: return 'badge bg-secondary';
      }
    },
    closeModal() {
      Modal.toggle('#chargesModal');
      this.$emit('onClose');
      this.resetModal();
    },
    resetModal() {
      this.subscriberLocal = null;
      this.activeTab = 'all';
    }
    ,
    async refreshCharges() {
      if (!this.subscriberLocal?.id_subscriber) return;
      try {
        const response = await http.get(`/places/${this.place.id_place}/subscribers/${this.subscriberLocal.id_subscriber}/charges`);
        this.subscriberLocal.charges = response.data || [];
      } catch (e) {
        // silencioso: o modal ainda mostra a lista anterior
      }
    },
    async reprocessMissingPayment() {
      if (!this.subscriberLocal?.id_subscriber) return;

      const method = this.subscriberLocal?.plan_payment_method === 'pix' ? 'PIX' : 'boleto';
      Loader.show(`Reprocessando ${method}`, 'Aguarde');

      try {
        const response = await http.post(
          `/places/${this.place.id_place}/subscribers/${this.subscriberLocal.id_subscriber}/reprocess-missing-payment`
        );

        const data = response.data?.data;
        if (data?.charges) {
          this.subscriberLocal.charges = data.charges;
        } else {
          await this.refreshCharges();
        }

        Alert.success(response.data?.message || 'Reprocessamento concluído.');

        const paymentDetails = response.data?.payment_details;
        if (this.subscriberLocal?.plan_payment_method === 'pix' && paymentDetails?.pix_code) {
          this.$emit('openPixModal', {
            pix_code: paymentDetails.pix_code,
            qr_code_base64: paymentDetails.pix_qrcode_img,
            payment_id: paymentDetails.id_card_trans,
            subscription_id: this.subscriberLocal?.id_subscriber,
            expires_at: paymentDetails.expires_at || paymentDetails.pix_expires_at || null,
          });
        }

        this.$emit('chargesUpdated');
      } catch (error) {
        Alert.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao reprocessar cobrança.');
      } finally {
        Loader.close();
      }
    },
    openNegotiation() {
      this.$refs.negotiationModal?.open();
    },
    async onNegotiationDone() {
      await this.refreshCharges();
      this.$emit('chargesUpdated');
    },
    canShowNegotiationDetails(charge) {
      return !!(charge?.id_negotiation_from || charge?.id_negotiation_to);
    },
    openNegotiationDetails(charge) {
      const negotiationId = charge?.id_negotiation_from || charge?.id_negotiation_to;
      this.$refs.negotiationDetailsModal?.open(negotiationId);
    },
  }
}
</script>

<template>
  <div class="modal fade" id="chargesModal" tabindex="-1" data-bs-keyboard="false"
       data-bs-backdrop="static" aria-labelledby="chargesModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">

        <div class="modal-header">
          <h5 class="modal-title" id="chargesModalLabel">Cobranças de {{ subscriberLocal?.user.user_name }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <div v-if="subscriberLocal?.charges.length">

            <button
              v-if="subscriberLocal?.plan_payment_method === 'bank_slip' || subscriberLocal?.plan_payment_method === 'money'"
              class="btn btn-sm btn-primary"
              type="button"
              @click="downloadInstallmentsCharge"
            >
              {{ subscriberLocal?.plan_payment_method === 'bank_slip' ? 'Baixar Carnê' : 'Imprimir Carnê' }}
            </button>

            <button
              v-if="canShowReprocessPayment"
              class="btn btn-sm btn-warning ms-2"
              type="button"
              @click="reprocessMissingPayment"
              title="Regerar carnê/boleto ou PIX quando não gerado ou com falha"
            >
              Reprocessar {{ subscriberLocal?.plan_payment_method === 'pix' ? 'PIX' : 'Boleto' }}
            </button>

            <button
              type="button"
              class="btn btn-sm btn-outline-primary ms-2"
              :disabled="extractLinkLoading"
              @click="copyExtractLink"
            >
              Gerar link
            </button>

            <button
              v-if="canNegotiate"
              class="btn btn-sm btn-success ms-2"
              type="button"
              @click="openNegotiation"
            >
              Negociar
            </button>

            <div class="schedule-workspace mt-4 mb-4">
              <div class="sw-chip-group">
                <button
                  v-for="tab in chargeTabs"
                  :key="tab.id"
                  type="button"
                  class="sw-chip"
                  :class="{ 'is-active': activeTab === tab.id }"
                  @click="activeTab = tab.id"
                >
                  {{ tab.label }}
                  <span class="sw-count">{{ chargesByTab[tab.id].length }}</span>
                </button>
              </div>
            </div>

            <table v-if="filteredCharges.length" class="table table-striped">
              <thead>
              <tr>
                <th>#</th>
                <th>Valor</th>
                <th>Vencimento</th>
                <th v-if="activeTab === 'all' || activeTab === 'receivable'">Forma</th>
                <th v-if="activeTab === 'all' || activeTab === 'receivable'">Status</th>
                <th>Referência</th>
                <template v-if="activeTab === 'received'">
                  <th>Pago em</th>
                  <th>Pgto realizado</th>
                  <th>Baixa por</th>
                </template>
                <th v-if="activeTab === 'cancelled'">Cancelado em</th>
                <th v-if="activeTab === 'unfolded'">Negociado em</th>
                <th>Ações</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="({ charge, originalIndex: index }, position) in filteredCharges" :key="charge.id_charge">
                <td>{{ position + 1 }}</td>
                <td>R$ {{ Number(charge.charge_amount).toFixed(2) }}</td>
                <td>{{ formatDate(charge.charge_due_date) }}</td>
                <td v-if="activeTab === 'all' || activeTab === 'receivable'">
                  {{ paymentMethodLabel(chargePaymentMethod(charge)) }}
                </td>
                <td v-if="activeTab === 'all' || activeTab === 'receivable'">
                    <span :class="statusClass(charge.charge_status)">
                      {{ charge.charge_status_dsc }}
                    </span>
                </td>
                <td>{{ charge.charge_reference }}</td>
                <template v-if="activeTab === 'received'">
                  <td>{{ charge.charge_paid_date ? formatDate(charge.charge_paid_date) : '-' }}</td>
                  <td>{{ charge.paid_payment_method ? paidPaymentMethodLabel(charge.paid_payment_method) : '-' }}</td>
                  <td>{{ manualDownUserName(charge) }}</td>
                </template>
                <td v-if="activeTab === 'cancelled'">{{ formatDate(charge.cancelled_at) }}</td>
                <td v-if="activeTab === 'unfolded'">{{ formatDate(charge.negotiated_at) }}</td>
                <td>
                  <button
                    v-if="canChangePaymentMethod(charge)"
                    type="button"
                    title="Alterar forma de pagamento"
                    @click="changeChargePaymentMethod(charge, index)"
                    class="btn btn-sm btn-outline-secondary btn-icon me-2"
                  >
                    <i class="bi bi-arrow-left-right"></i>
                  </button>

                  <button
                    v-if="canChangeDueDate(charge)"
                    type="button"
                    title="Alterar vencimento"
                    @click="changeChargeDueDate(charge, index)"
                    class="btn btn-sm btn-outline-secondary btn-icon me-2"
                  >
                    <i class="bi bi-calendar-event"></i>
                  </button>

                  <button
                    v-if="chargePaymentMethod(charge) === 'pix' && !charge.charge_is_downloaded && charge.charge_status != 'cancelled' && charge.charge_status != 'negotiated'"
                    type="button"
                    title="Pagar com PIX"
                    @click="payChargeWithPix(charge)"
                    class="btn btn-sm btn-info btn-icon me-2"
                  >
                    <i class="bi bi-qr-code"></i>
                  </button>

                  <button
                    type="button"
                    title="Gerar PDF"
                    v-else-if="!charge.charge_is_downloaded && charge.charge_status != 'cancelled' && charge.charge_status != 'negotiated'"
                    @click="downloadInstallmentCharge(charge, index)"
                          class="btn btn-sm btn-info btn-icon me-2">
                    <i class="bi bi-file-pdf"></i>
                  </button>
                  <button type="button" title="Gerar Comprovante" v-if="charge.charge_status == 'paid'"
                          @click="downloadReceiptCharge(charge)"
                          class="btn btn-sm btn-success btn-icon me-2">
                    <i class="bi bi-file-pdf"></i>
                  </button>
                  <button type="button" title="Marcar como pago (Baixa)" v-if="!charge.charge_is_downloaded && charge.charge_status != 'cancelled' && charge.charge_status != 'negotiated'"
                          @click="downCharge(charge, index)"
                          class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400">
                    <i class="bi bi-arrow-down-circle"></i>
                  </button>
                  <button type="button" title="Estornar baixa" v-else-if="charge.charge_is_downloaded"
                          @click="undownCharge(charge, index)"
                          class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400">
                    <i class="bi bi-arrow-down-circle"></i>
                  </button>

                  <button
                    v-if="canShowNegotiationDetails(charge)"
                    type="button"
                    title="Ver detalhes da negociação"
                    @click="openNegotiationDetails(charge)"
                    class="btn btn-sm btn-primary btn-icon me-2"
                  >
                    <i class="bi bi-info-circle"></i>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
            <p v-else class="text-center text-muted py-4">Nenhuma parcela nesta aba.</p>
          </div>
          <div v-else class="text-center">
            <p>Nenhuma cobrança encontrada para este assinante.</p>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="extractLinkLoading"
              @click="copyExtractLink"
            >
              Gerar link
            </button>
            <button
              v-if="canNegotiate"
              class="btn btn-sm btn-warning ms-2"
              type="button"
              @click="openNegotiation"
            >
              Negociar
            </button>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Fechar</button>
        </div>

      </div>
    </div>
  </div>

  <ChargeNegotiationModal
    ref="negotiationModal"
    :subscriber="subscriberLocal"
    :placeId="place.id_place"
    @negotiationDone="onNegotiationDone"
  />

  <ChargeNegotiationDetailsModal
    ref="negotiationDetailsModal"
    :subscriber="subscriberLocal"
    :placeId="place.id_place"
  />
</template>

<style scoped>

</style>
